import { Fragment, type ReactNode } from 'react'
import Box from '@mui/material/Box'

type Segment = { text: string; bold: boolean; italic: boolean }

function parseSegments(stored: string): Segment[] {
  const trimmed = stored.trim()
  if (!trimmed) return []

  const triple = /^\*\*\*(.+)\*\*\*$/.exec(trimmed)
  if (triple) return [{ text: triple[1], bold: true, italic: true }]

  const bold = /^\*\*(.+)\*\*$/.exec(trimmed)
  if (bold && !trimmed.includes('***')) {
    return [{ text: bold[1], bold: true, italic: false }]
  }

  const italic = /^\*(.+)\*$/.exec(trimmed)
  if (italic && !trimmed.startsWith('**')) {
    return [{ text: italic[1], bold: false, italic: true }]
  }

  return [{ text: stored, bold: false, italic: false }]
}

interface SurveyMarkdownTextProps {
  readonly text: string
  readonly component?: 'p' | 'span' | 'h2'
  readonly sx?: object
  readonly 'data-testid'?: string
}

/** Renders Sensemaker whole-field markdown (`**bold**`, `*italic*`, `***both***`). */
export function SurveyMarkdownText({
  text,
  component = 'span',
  sx,
  'data-testid': testId,
}: SurveyMarkdownTextProps) {
  const segments = parseSegments(text)
  const content: ReactNode = segments.map((seg) => (
    <Box
      key={`${seg.bold}-${seg.italic}-${seg.text}`}
      component="span"
      sx={{
        fontWeight: seg.bold ? 700 : undefined,
        fontStyle: seg.italic ? 'italic' : undefined,
      }}
    >
      {seg.text}
    </Box>
  ))

  const inlineSx = component === 'span' ? { display: 'inline', ...sx } : sx

  return (
    <Box component={component} sx={inlineSx} data-testid={testId}>
      {segments.length === 1 ? content : <Fragment>{content}</Fragment>}
    </Box>
  )
}
